import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Mail, Clock, Car, Train, Navigation } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const IMAGES = {
  interior: 'https://customer-assets.emergentagent.com/job_vino-vibes/artifacts/sej0xcfn_FB_IMG_1770407274720.jpg',
};

const Locatie = () => {
  const { t } = useLanguage();

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-24" data-testid="locatie-page">
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-body text-sm tracking-[0.2em] text-copper mb-4 uppercase"
          >
            {t.locationPage.subtitle}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl font-bold text-[#EDEDED] mb-6"
          >
            {t.locationPage.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-body text-[#A1A1A1] text-lg max-w-2xl mx-auto"
          >
            {t.locationPage.description}
          </motion.p>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-full h-[400px] md:h-[500px] overflow-hidden rounded-sm border border-[#2A2A2A]"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2502.8876!2d2.8167!3d51.1847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47dcb9d7b3c9f8b5%3A0x1234567890abcdef!2sParijsstraat%2026%2C%208430%20Middelkerke%2C%20Belgium!5e0!3m2!1snl!2sbe!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(80%) contrast(1.1) brightness(0.9)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BARBAAR Locatie"
              data-testid="location-map"
            />
          </motion.div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-8"
            >
              <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center mb-6">
                <MapPin className="w-6 h-6 text-copper" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-4">{t.locationPage.address}</h3>
              <address className="font-body text-[#A1A1A1] not-italic leading-relaxed">
                <strong className="text-[#EDEDED]">BARBAAR</strong><br />
                Parijsstraat 26<br />
                8430 Middelkerke<br />
                Belgium
              </address>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Parijsstraat+26,+8430+Middelkerke,+Belgium"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 text-copper hover:text-copper-light transition-colors font-body text-sm"
                data-testid="get-directions-btn"
              >
                <Navigation className="w-4 h-4" />
                {t.locationPage.planRoute}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-8"
            >
              <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-copper" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-4">{t.locationPage.openingHours}</h3>
              <div className="space-y-3 font-body text-[#A1A1A1] text-sm">
                <div className="flex justify-between">
                  <span>{t.days.monday}</span>
                  <span className="text-copper">11u30 - 22u30</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.tuesday}</span>
                  <span className="text-copper">11u30 - 22u30</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.wednesday}</span>
                  <span className="text-[#666]">{t.locationPage.closed}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.thursday}</span>
                  <span className="text-[#666]">{t.locationPage.closed}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.friday}</span>
                  <span className="text-copper">11u30 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.saturday}</span>
                  <span className="text-copper">11u30 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.sunday}</span>
                  <span className="text-copper">11u30 - 22u30</span>
                </div>
                <p className="text-copper text-xs pt-1">{t.locationPage.kitchenNote}</p>
                <p className="text-copper text-xs pt-2 font-semibold">{t.locationPage.summerTitle}</p>
                <div className="flex justify-between">
                  <span>{t.days.monday}</span>
                  <span className="text-[#666]">{t.locationPage.closed}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.tuesday}</span>
                  <span className="text-copper">12u00 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.wednesday}</span>
                  <span className="text-copper">12u00 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.thursday}</span>
                  <span className="text-copper">12u00 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.friday}</span>
                  <span className="text-copper">12u00 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.saturday}</span>
                  <span className="text-copper">12u00 - 00u00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t.days.sunday}</span>
                  <span className="text-copper">12u00 - 00u00</span>
                </div>
                <p className="text-copper text-xs pt-1">{t.locationPage.summerKitchenNote}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="glass-card p-8"
            >
              <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center mb-6">
                <Phone className="w-6 h-6 text-copper" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-4">{t.locationPage.contact}</h3>
              <div className="space-y-4">
                <a
                  href="tel:+32000000000"
                  className="flex items-center gap-3 text-[#A1A1A1] hover:text-copper transition-colors font-body"
                >
                  <Phone className="w-4 h-4" />
                  +32 (0) XX XX XX XX
                </a>
                <a
                  href="mailto:info@barbaarmiddelkerke.com"
                  className="flex items-center gap-3 text-[#A1A1A1] hover:text-copper transition-colors font-body"
                >
                  <Mail className="w-4 h-4" />
                  info@barbaarmiddelkerke.com
                </a>
              </div>
              <Button
                asChild
                className="w-full mt-6 bg-copper hover:bg-copper-dark text-white font-body text-sm tracking-wider py-5 rounded-sm"
                data-testid="contact-us-btn"
              >
                <Link to="/contact">
                  {t.locationPage.sendMessage}
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-4">
              {t.locationPage.howToReach}
            </h2>
            <p className="font-body text-[#A1A1A1] max-w-2xl mx-auto">
              {t.locationPage.howToReachDesc}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Car className="w-6 h-6 text-copper" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-3">{t.locationPage.byCar}</h3>
                  <p className="font-body text-[#A1A1A1] leading-relaxed">
                    {t.locationPage.byCarDesc}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Train className="w-6 h-6 text-copper" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#EDEDED] mb-3">{t.locationPage.byTram}</h3>
                  <p className="font-body text-[#A1A1A1] leading-relaxed">
                    {t.locationPage.byTramDesc}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-sm tracking-[0.2em] text-copper mb-4 uppercase">{t.locationPage.welcomeSubtitle}</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-6">
                {t.locationPage.welcomeTitle}
              </h2>
              <p className="font-body text-[#A1A1A1] text-lg leading-relaxed mb-8">
                {t.locationPage.welcomeDesc}
              </p>
              <Button
                asChild
                className="bg-copper hover:bg-copper-dark text-white font-body text-sm tracking-wider px-8 py-6 rounded-sm btn-copper"
                data-testid="view-menu-btn"
              >
                <Link to="/menu">
                  {t.locationPage.viewMenu}
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.interior}
                  alt="BARBAAR interieur"
                  className="w-full h-full object-cover image-hover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 border border-copper opacity-30" />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Locatie;
